import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import AuthPage from './pages/AuthPage';
import RoomPage from './pages/RoomPage';
import FloorPage from './pages/FloorPage';
import IntakePage from './pages/IntakePage';
import { ToastHost } from './components/Toast';

// Set to true to skip login + room selection during local dev. Set back to false before deploying.
const DEV_MODE = false;

const DEV_SESSION = {
  user: { id: '00000000-0000-0000-0000-000000000001', email: 'dev@shoptrack.local', user_metadata: { full_name: 'Dev User' } }
};

const DEV_ROOM = {
  id: '00000000-0000-0000-0000-000000000002',
  name: 'Dev Shop',
  shop_name: 'Dev Shop',
  code: 'DEVROOM',
  owner_id: '00000000-0000-0000-0000-000000000001',
  role: 'admin',
  logo_url: null,
};

// Public client intake route (/intake/<token>) — rendered with no auth, even in DEV_MODE
const INTAKE_TOKEN = (window.location.pathname.match(/^\/intake\/([A-Za-z0-9]+)\/?$/) || [])[1] || null;

export default function App() {
  const [session, setSession] = useState(null);
  const [room, setRoom] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(true);
  // Stays false until we've answered "which room is this user in?" —
  // prevents the login/room screen flashing before the floor on app launch
  const [roomChecked, setRoomChecked] = useState(false);

  useEffect(() => {
    if (INTAKE_TOKEN) return;
    if (DEV_MODE) {
      setSession(DEV_SESSION);
      setRoom(DEV_ROOM);
      setLoading(false);
      setRoomChecked(true);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (INTAKE_TOKEN || DEV_MODE) return;
    if (session) {
      setRoomChecked(false);
      restoreRoom(session.user.id).finally(() => setRoomChecked(true));
    } else {
      setRoom(null);
      setRoomChecked(true);
    }
  }, [session]);

  async function restoreRoom(userId) {
    const cachedRoomId = localStorage.getItem('shoptrack_room_id');
    if (cachedRoomId) {
      const loaded = await loadRoom(cachedRoomId, userId);
      if (loaded) return;
      localStorage.removeItem('shoptrack_room_id');
    }
    const { data: membership } = await supabase
      .from('room_members')
      .select('*, rooms(*)')
      .eq('user_id', userId)
      .order('joined_at', { ascending: false })
      .limit(1)
      .single();
    if (membership?.rooms) {
      const roomData = { ...membership.rooms, role: membership.role };
      localStorage.setItem('shoptrack_room_id', roomData.id);
      setRoom(roomData);
    }
  }

  async function loadRoom(roomId, userId) {
    const { data: membership } = await supabase
      .from('room_members')
      .select('*, rooms(*)')
      .eq('room_id', roomId)
      .eq('user_id', userId)
      .single();
    if (membership?.rooms) {
      setRoom({ ...membership.rooms, role: membership.role });
      return true;
    }
    return false;
  }

  function handleJoinRoom(roomData) {
    localStorage.setItem('shoptrack_room_id', roomData.id);
    setRoom(roomData);
    setSwitching(false);
  }

  let content;
  if (INTAKE_TOKEN) {
    content = <IntakePage token={INTAKE_TOKEN} />;
  } else if (loading || (session && !room && !roomChecked)) {
    content = (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c0c0e' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  } else if (!session) {
    content = <AuthPage />;
  } else if (!room || switching) {
    content = (
      <RoomPage
        session={session}
        onJoin={handleJoinRoom}
        currentRoom={switching ? room : null}
      />
    );
  } else {
    content = (
      <FloorPage
        session={session}
        room={room}
        setRoom={setRoom}
        onSwitchRoom={() => setSwitching(true)}
      />
    );
  }

  return (<>{content}<ToastHost /></>);
}
